import {
  contractStatusLabels,
  deadlineCountdown,
  type ContractViewStatus,
} from "@car/api/contract-config";
import type { AppRouterClient } from "@car/api/routers/index";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@car/ui/components/accordion";
import { Badge } from "@car/ui/components/badge";
import { Button } from "@car/ui/components/button";
import { Card, CardContent } from "@car/ui/components/card";
import { Checkbox } from "@car/ui/components/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@car/ui/components/dialog";
import { FormAlert, useAppForm } from "@car/ui/components/form";
import { Label } from "@car/ui/components/label";
import { Skeleton } from "@car/ui/components/skeleton";
import { requiredText } from "@car/ui/lib/form-rules";
import { cn, SEP } from "@car/ui/lib/utils";
import {
  Attachment01Icon,
  Cancel01Icon,
  ContractsIcon,
  HourglassIcon,
  SignatureIcon,
} from "@hugeicons-pro/core-stroke-rounded";
import { HugeiconsIcon } from "@hugeicons/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";
import { z } from "zod";

import { DocumentSigners, PageHeader, PanelEmpty, PanelQueryError } from "@/components/panel-bits";
import { faDate, faDateTimeShort, faNumber } from "@/lib/format";
import { orpc } from "@/utils/orpc";

export const Route = createFileRoute("/panel/contracts")({
  head: () => ({ meta: [{ title: "قراردادها | پنل مشتریان تابان خودرو" }] }),
  component: ContractsPage,
});

type PanelContract = Awaited<
  ReturnType<AppRouterClient["panel"]["contracts"]["list"]>
>["contracts"][number];

const statusClass = {
  pending: "bg-amber-500/15 text-amber-800 dark:text-amber-400",
  signed: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400",
  rejected: "bg-destructive/10 text-destructive",
  expired: "bg-muted text-muted-foreground",
  cancelled: "bg-muted text-muted-foreground",
} satisfies Record<ContractViewStatus, string>;

function ContractsPage() {
  const query = useQuery(orpc.panel.contracts.list.queryOptions());
  const contracts = query.data?.contracts ?? [];
  const open = contracts.filter((item) => item.status === "pending");
  const past = contracts.filter((item) => item.status !== "pending");

  return (
    <div>
      <PageHeader
        title="قراردادها"
        description="قراردادهایی که تابان خودرو برای امضای شما صادر کرده است؛ متن را با دقت بخوانید و تا پایان مهلت تأیید یا رد کنید."
      />

      {query.isPending ? (
        <div className="grid gap-4">
          <Skeleton className="h-56 rounded-xl" />
          <Skeleton className="h-32 rounded-xl" />
        </div>
      ) : query.isError ? (
        <PanelQueryError title="قراردادها بارگیری نشد" query={query} />
      ) : contracts.length === 0 ? (
        <PanelEmpty
          icon={ContractsIcon}
          title="قراردادی برای شما صادر نشده است"
          description="وقتی کارشناسان ما قراردادی برای امضای شما صادر کنند، پیامک می‌گیرید و همین‌جا می‌توانید آن را بخوانید و امضا کنید."
        />
      ) : (
        <div className="grid gap-8">
          {open.length > 0 ? (
            <section className="grid gap-4" aria-labelledby="open-contracts">
              <h2 id="open-contracts" className="font-black">
                در انتظار امضای شما ({faNumber(open.length)})
              </h2>
              {open.map((item) => (
                <ContractCard key={item.id} contract={item} />
              ))}
            </section>
          ) : null}
          {past.length > 0 ? (
            <section className="grid gap-3" aria-labelledby="past-contracts">
              <h2 id="past-contracts" className="font-black">
                سوابق قراردادها
              </h2>
              {past.map((item) => (
                <ContractCard key={item.id} contract={item} />
              ))}
            </section>
          ) : null}
        </div>
      )}
    </div>
  );
}

/** Where the contract stands, in one line under its title */
function outcomeText(contract: PanelContract) {
  switch (contract.status) {
    case "pending":
      return `مهلت امضا تا ${faDateTimeShort(contract.deadlineIso)}`;
    case "signed":
      return contract.respondedIso ? `امضا شده در ${faDateTimeShort(contract.respondedIso)}` : "";
    case "rejected":
      return contract.respondedIso ? `رد شده در ${faDateTimeShort(contract.respondedIso)}` : "";
    case "expired":
      return `مهلت امضا ${faDateTimeShort(contract.deadlineIso)} به پایان رسید`;
    case "cancelled":
      return contract.cancelledIso
        ? `در ${faDateTimeShort(contract.cancelledIso)} توسط تابان خودرو لغو شد`
        : "";
  }
}

function ContractCard({ contract }: { contract: PanelContract }) {
  const pending = contract.status === "pending";
  return (
    <Card className={cn(pending && "border-amber-500/40")}>
      <CardContent className="grid gap-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="grid gap-1">
            <span className="font-black">{contract.title}</span>
            <span className="text-xs text-muted-foreground">
              شماره قرارداد <span dir="ltr">{contract.contractNumber}</span>
              {contract.orderNumber ? (
                <>
                  {SEP}سفارش <span dir="ltr">{contract.orderNumber}</span>
                </>
              ) : null}
              {SEP}صدور {faDate(contract.createdIso)}
            </span>
            <span className="text-sm text-muted-foreground">{outcomeText(contract)}</span>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {pending ? (
              <Badge className={statusClass.pending}>
                <HugeiconsIcon icon={HourglassIcon} strokeWidth={1.8} />
                {deadlineCountdown(contract.deadlineIso)}
              </Badge>
            ) : (
              <Badge className={statusClass[contract.status]}>
                {contractStatusLabels[contract.status]}
              </Badge>
            )}
          </div>
        </div>

        {contract.status === "rejected" && contract.rejectReason ? (
          <p className="rounded-lg bg-muted/60 p-3 text-sm">
            <span className="font-bold">دلیل رد: </span>
            {contract.rejectReason}
          </p>
        ) : null}
        {contract.status === "cancelled" && contract.cancelReason ? (
          <p className="rounded-lg bg-muted/60 p-3 text-sm">
            <span className="font-bold">دلیل لغو: </span>
            {contract.cancelReason}
          </p>
        ) : null}

        <Accordion defaultValue={pending ? ["terms"] : []}>
          <AccordionItem value="terms" className="border-b-0">
            <AccordionTrigger className="py-2 hover:no-underline">
              <span className="font-bold">متن قرارداد</span>
            </AccordionTrigger>
            <AccordionContent className="pb-2">
              <div
                tabIndex={0}
                className="max-h-96 overflow-y-auto rounded-lg border bg-muted/30 p-4 text-sm leading-7 whitespace-pre-wrap"
              >
                {contract.body}
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
        <DocumentSigners signers={contract.signers} />

        <div className="flex flex-wrap items-center gap-2">
          {contract.file ? <ContractFileDialog contract={contract} /> : null}
          {pending ? (
            <>
              <SignDialog contract={contract} />
              <RejectDialog contract={contract} />
            </>
          ) : null}
        </div>
        {pending ? (
          <p className="text-xs text-muted-foreground">
            روش امضا: {contract.providerLabel}. با تأیید یا رد، زمان پاسخ، نشانی اینترنتی و مرورگر
            شما به‌عنوان شواهد پاسخ در پرونده قرارداد ثبت می‌شود.
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
}

/** Opens the contract's file with fresh short-lived links, only while the dialog is open */
function ContractFileDialog({ contract }: { contract: PanelContract }) {
  const [open, setOpen] = useState(false);
  const query = useQuery({
    ...orpc.panel.contracts.fileLink.queryOptions({ input: { id: contract.id } }),
    enabled: open,
    // the links are signed for five minutes: every opening asks for fresh ones
    staleTime: 0,
    gcTime: 0,
  });
  const file = query.data;
  const isPdf = contract.file?.mimeType === "application/pdf";
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger render={<Button variant="outline" size="sm" />}>
        <HugeiconsIcon icon={Attachment01Icon} strokeWidth={1.8} />
        فایل قرارداد
      </DialogTrigger>
      <DialogContent lang="fa" dir="rtl" className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{contract.title}</DialogTitle>
          <DialogDescription>
            {contract.file
              ? `${contract.file.name}${SEP}${faNumber(Math.max(1, Math.round(contract.file.size / 1024)))} کیلوبایت`
              : ""}
          </DialogDescription>
        </DialogHeader>
        {query.isError || (query.isSuccess && !file) ? (
          <p className="text-sm text-destructive">فایل باز نشد؛ کمی بعد دوباره تلاش کنید.</p>
        ) : !file ? (
          <Skeleton className="h-64 rounded-lg" />
        ) : isPdf ? (
          <iframe
            title={contract.title}
            src={file.url}
            className="h-[70vh] w-full rounded-lg border"
          />
        ) : (
          <img
            src={file.url}
            alt={contract.title}
            className="max-h-[70vh] w-full rounded-lg object-contain"
          />
        )}
        {file ? (
          <DialogFooter>
            <Button
              variant="outline"
              nativeButton={false}
              render={<a href={file.downloadUrl} download={file.name} rel="noopener" />}
            >
              دانلود
            </Button>
          </DialogFooter>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

/** Refreshes the list, the nav badge and the notification centre after an answer */
function useAnswer(onDone: () => void, setError: (message: string) => void) {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.panel.contracts.answer.mutationOptions({
      onSuccess: (result) => {
        queryClient.invalidateQueries({ queryKey: orpc.panel.key() });
        if (!result.ok) {
          setError(result.reason);
          return;
        }
        onDone();
      },
      onError: () => setError("پاسخ ثبت نشد؛ اتصال را بررسی و دوباره تلاش کنید."),
    }),
  );
}

const signSchema = z.object({
  accepted: z.boolean().refine((value) => value, "برای امضا، پذیرش متن قرارداد لازم است"),
});

function SignDialog({ contract }: { contract: PanelContract }) {
  const [open, setOpen] = useState(false);
  const [error, setError] = useState("");
  const mutation = useAnswer(() => {
    toast.success("قرارداد امضا شد");
    setOpen(false);
  }, setError);
  const form = useAppForm({
    defaultValues: { accepted: false },
    validators: { onBlur: signSchema },
    onSubmit: async () => {
      setError("");
      await mutation
        .mutateAsync({ id: contract.id, answer: { decision: "sign", accepted: true } })
        .catch(() => undefined);
    },
  });
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (next) {
          form.reset({ accepted: false });
          setError("");
        }
        setOpen(next);
      }}
    >
      <DialogTrigger render={<Button size="sm" />}>
        <HugeiconsIcon icon={SignatureIcon} strokeWidth={1.8} />
        امضا و تأیید
      </DialogTrigger>
      <DialogContent lang="fa" dir="rtl">
        <form.AppForm>
          <form.Form className="grid gap-4">
            <DialogHeader>
              <DialogTitle>امضای «{contract.title}»</DialogTitle>
              <DialogDescription>
                امضا نهایی است و پس از ثبت قابل برگشت نیست. پیش از تأیید، متن قرارداد
                {contract.file ? " و فایل پیوست آن" : ""} را کامل بخوانید.
              </DialogDescription>
            </DialogHeader>
            <form.AppField name="accepted">
              {(field) => (
                <field.FormField label="تأیید نهایی">
                  {({ controlProps }) => (
                    <div className="flex items-start gap-2.5 rounded-lg border p-3">
                      <Checkbox
                        {...controlProps}
                        checked={field.state.value}
                        onCheckedChange={(checked) => {
                          field.handleChange(checked);
                          field.handleBlur();
                        }}
                        className="mt-0.5"
                      />
                      <Label htmlFor={controlProps.id} className="leading-6 font-normal">
                        متن قرارداد را خواندم و می‌پذیرم
                      </Label>
                    </div>
                  )}
                </field.FormField>
              )}
            </form.AppField>
            <FormAlert message={error} />
            <DialogFooter>
              <form.SubmitButton pending={mutation.isPending}>
                <HugeiconsIcon icon={SignatureIcon} strokeWidth={1.8} />
                امضای قرارداد
              </form.SubmitButton>
            </DialogFooter>
          </form.Form>
        </form.AppForm>
      </DialogContent>
    </Dialog>
  );
}

const rejectSchema = z.object({ reason: requiredText("دلیل رد", { min: 3, max: 500 }) });

function RejectDialog({ contract }: { contract: PanelContract }) {
  const [open, setOpen] = useState(false);
  const [error, setError] = useState("");
  const mutation = useAnswer(() => {
    toast.success("رد قرارداد ثبت شد؛ کارشناسان ما با شما تماس می‌گیرند");
    setOpen(false);
  }, setError);
  const form = useAppForm({
    defaultValues: { reason: "" },
    validators: { onBlur: rejectSchema },
    onSubmit: async ({ value }) => {
      setError("");
      await mutation
        .mutateAsync({
          id: contract.id,
          answer: { decision: "reject", reason: value.reason.trim() },
        })
        .catch(() => undefined);
    },
  });
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (next) {
          form.reset({ reason: "" });
          setError("");
        }
        setOpen(next);
      }}
    >
      <DialogTrigger render={<Button variant="outline" size="sm" />}>
        <HugeiconsIcon icon={Cancel01Icon} strokeWidth={1.8} />
        رد قرارداد
      </DialogTrigger>
      <DialogContent lang="fa" dir="rtl">
        <form.AppForm>
          <form.Form className="grid gap-4">
            <DialogHeader>
              <DialogTitle>رد «{contract.title}»</DialogTitle>
              <DialogDescription>
                دلیل را بنویسید تا کارشناس فروش بررسی کند و در صورت نیاز قرارداد اصلاح‌شده صادر شود.
                رد قرارداد قابل برگشت نیست.
              </DialogDescription>
            </DialogHeader>
            <form.AppField name="reason">
              {(field) => (
                <field.TextareaField
                  label="دلیل رد"
                  rows={4}
                  placeholder="مثلاً مبلغ پیش‌پرداخت با توافق ما فرق دارد."
                />
              )}
            </form.AppField>
            <FormAlert message={error} />
            <DialogFooter>
              <form.SubmitButton variant="destructive" pending={mutation.isPending}>
                ثبت رد قرارداد
              </form.SubmitButton>
            </DialogFooter>
          </form.Form>
        </form.AppForm>
      </DialogContent>
    </Dialog>
  );
}
